import React from 'react';
import { FiUsers, FiUserCheck, FiCalendar, FiDollarSign, FiCreditCard, FiTrendingUp } from 'react-icons/fi';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useDashboardStats } from '../hooks/useDashboardStats';

const Dashboard = ({ user }) => {
  const { data: dashboardData, isLoading, error } = useDashboardStats();
  console.log("dashboardData",dashboardData);

  const stats = dashboardData?.data;

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle, isFullWidth = false }) => (
    <div className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 hover:border-gray-200 transform hover:-translate-y-3 hover:scale-105 overflow-hidden ${isFullWidth ? 'w-full' : ''}`}>
      {/* Animated background gradient */}
      <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br from-${color}-50/40 via-${color}-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none`}></div>
      
      {/* Shimmer effect */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none"></div>
      
      {/* Top border accent */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${color}-400 to-${color}-600 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
      
      <div className="relative flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-3">
            <div className={`w-2 h-2 rounded-full bg-${color}-500 opacity-60 group-hover:opacity-100 transition-opacity duration-300 animate-pulse-dot`}></div>
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide transition-all duration-300 group-hover:text-gray-800">
              {title}
            </p>
          </div>
          <p className="text-4xl font-bold text-gray-900 mb-2 transition-all duration-500 group-hover:scale-105 group-hover:text-gray-800">
            {value}
          </p>
          {subtitle && (
            <p className="text-sm text-gray-500 transition-all duration-300 group-hover:text-gray-700 group-hover:font-medium">
              {subtitle}
            </p>
          )}
        </div>
        <div className={`relative p-5 rounded-3xl bg-gradient-to-br from-${color}-100 via-${color}-200 to-${color}-300 transition-all duration-500 group-hover:scale-125 group-hover:rotate-6 group-hover:shadow-xl`}>
          <Icon className={`h-8 w-8 text-${color}-600 transition-all duration-300 group-hover:text-${color}-800 group-hover:scale-110`} />
          {/* Icon glow effect */}
          <div className={`absolute inset-0 rounded-3xl bg-${color}-400/30 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
          {/* Icon pulse effect */}
          <div className={`absolute inset-0 rounded-3xl bg-${color}-400/20 animate-ping opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
        </div>
      </div>
      
      {/* Corner accent */}
      <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-${color}-200/30 to-transparent rounded-bl-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
      
      {/* Bottom accent line */}
      <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-${color}-300 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
    </div>
  );

  const StatSection = ({ title, children, isTwoItems = false }) => (
    <div className="space-y-8">
      <div className="flex items-center space-x-4">
        <div className="h-1 w-12 bg-gradient-to-r from-primary-500 via-primary-600 to-indigo-600 rounded-full shadow-sm"></div>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent tracking-tight">
          {title}
        </h3>
        <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent"></div>
      </div>
      <div className={`grid gap-8 ${isTwoItems ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {children}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Layout user={user}>
        <PageHeader
          title="Dashboard"
          subtitle="Welcome to your Lynx dashboard"
          user={user}
        />
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200"></div>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent absolute top-0 left-0" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
          </div>
          <div className="text-center">
            <p className="text-lg font-medium text-gray-700 animate-pulse">Loading dashboard...</p>
            <p className="text-sm text-gray-500 mt-1">Fetching your data</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout user={user}>
        <PageHeader
          title="Dashboard"
          subtitle="Welcome to your Lynx dashboard"
          user={user}
        />
        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-6 shadow-lg animate-fade-in">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                {/* <FiX className="h-5 w-5 text-red-600" /> */}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-800">Error Loading Dashboard</h3>
              <p className="text-red-700 mt-1">{error.message}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <PageHeader
        title="Dashboard"
        subtitle="Welcome to your Lynx dashboard"
        user={user}
      />
      
      <div className="pb-12">
        {/* Welcome Section */}
        <div className="relative bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 p-8 border border-gray-100 overflow-hidden animate-fade-in">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-200/20 to-primary-300/20 rounded-full -translate-y-16 translate-x-16 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-200/20 to-indigo-300/20 rounded-full translate-y-12 -translate-x-12 animate-pulse" style={{animationDelay: '1s'}}></div>
          
          <div className="relative text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl mb-4 shadow-lg animate-bounce" style={{animationDuration: '2s'}}>
              <FiTrendingUp className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent mb-3 animate-fade-in-up">
              Welcome to Lynx Dashboard
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              Your central hub for managing and monitoring all aspects of your application. 
              Get started by exploring the features available in the sidebar.
            </p>
          </div>
        </div>

        {/* Main Statistics */}
                 <div className="py-4">
           <StatSection title="Overview" isTwoItems={true}>
             <div className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
               <StatCard
                 title="Total Companies"
                 value={stats?.companies_count || 0}
                 icon={FiUsers}
                 color="blue"
                 isFullWidth={true}
               />
             </div>
             <div className="animate-fade-in-up" style={{animationDelay: '0.2s'}}>
               <StatCard
                 title="Total Students"
                 value={stats?.students_count || 0}
                 icon={FiUserCheck}
                 color="blue"
                 isFullWidth={true}
               />
             </div>
           </StatSection>
         </div>

        {/* Events Statistics */}
                 <div className="py-4">
           <StatSection title="Events" isTwoItems={true}>
             <div className="animate-fade-in-up" style={{animationDelay: '0.3s'}}>
               <StatCard
                 title="Total Events"
                 value={stats?.events?.count || 0}
                 icon={FiCalendar}
                 color="blue"
                 isFullWidth={true}
               />
             </div>
             <div className="animate-fade-in-up" style={{animationDelay: '0.4s'}}>
               <StatCard
                 title="Event Requests"
                 value={stats?.events?.requests_count || 0}
                 icon={FiTrendingUp}
                 color="blue"
                 isFullWidth={true}
               />
             </div>
           </StatSection>
         </div>

        {/* Subscriptions Statistics */}
        <div className="py-4">
          <StatSection title="Subscriptions">
            <div className="animate-fade-in-up" style={{animationDelay: '0.5s'}}>
              <StatCard
                title="Total Subscriptions"
                value={stats?.subscriptions?.count || 0}
                icon={FiDollarSign}
                color="blue"
              />
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: '0.6s'}}>
              <StatCard
                title="Active Subscriptions"
                value={stats?.subscriptions?.active_count || 0}
                icon={FiUserCheck}
                color="green"
              />
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: '0.7s'}}>
              <StatCard
                title="Inactive Subscriptions"
                value={stats?.subscriptions?.inactive_count || 0}
                icon={FiUsers}
                color="red"
              />
            </div>
          </StatSection>
        </div>

        {/* Transactions Statistics */}
                 <div className="py-4">
           <StatSection title="Transactions">
             <div className="animate-fade-in-up" style={{animationDelay: '0.8s'}}>
               <StatCard
                 title="Total Transactions"
                 value={stats?.transactions?.count || 0}
                 icon={FiCreditCard}
                 color="blue"
               />
             </div>
             <div className="animate-fade-in-up" style={{animationDelay: '0.9s'}}>
               <StatCard
                 title="Total Paid Amount"
                 value={`$${stats?.transactions?.total_paid_amount?.toLocaleString() || 0}`}
                 icon={FiDollarSign}
                 color="green"
                 subtitle="Successfully processed"
               />
             </div>
             <div className="animate-fade-in-up" style={{animationDelay: '1.0s'}}>
               <StatCard
                 title="Total Unpaid Amount"
                 value={`$${stats?.transactions?.total_unpaid_amount?.toLocaleString() || 0}`}
                 icon={FiTrendingUp}
                 color="red"
                 subtitle="Pending payments"
               />
             </div>
           </StatSection>
         </div>
      </div>
    </Layout>
  );
};

export default Dashboard;